import * as Icons from "lucide-react";
import { SuggestionsBell } from "./SuggestionsBell";
import { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { STUDIO_NAV, ADMIN_NAV, CLIENTE_NAV, ROLE_LABELS } from "../lib/nav";
import { useDisabledModules } from "../lib/moduli";
import { Shield, LogOut, ChevronDown, Paperclip, UserCog } from "lucide-react";
import { MSG_UNREAD_EVENT, ATT_UNREAD_EVENT } from "./NewMessageNotifier";
import { useStoredWidth, ResizeHandle } from "./ResizeHandle";
import { RemoteSupportMenuItem } from "./RemoteSupportMenuItem";
import FirmaGraficaDialog from "./FirmaGraficaDialog";

function Icon({ name, className }) {
  const C = Icons[name] || Icons.Circle;
  return <C className={className} />;
}

export default function Sidebar({ collapsed = false }) {
  const [sideW, setSideW] = useStoredWidth("pgp_w_sidebar", 256, 200, 420);
  const { user, logout, can } = useAuth();
  const [profiloOpen, setProfiloOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [mailUnread, setMailUnread] = useState({ email: 0, pec: 0 });
  const [praticheAperte, setPraticheAperte] = useState(0);
  const [praticheArchiviate, setPraticheArchiviate] = useState(0);
  useEffect(() => {
    if (!user || user.role === "cliente" || user.role === "superadmin") return;
    if (!can("pratiche")) return;
    const fetchCount = () => api.get("/studio/pratiche/count-aperte").then(({ data }) => setPraticheAperte(data.count || 0)).catch(() => {});
    const fetchArch = () => api.get("/studio/pratiche/count-archiviate").then(({ data }) => setPraticheArchiviate(data.count || 0)).catch(() => {});
    fetchCount();
    fetchArch();
    const id = setInterval(() => { fetchCount(); fetchArch(); }, 5 * 60 * 1000);
    window.addEventListener("pgp-archivio-changed", fetchArch);
    return () => { clearInterval(id); window.removeEventListener("pgp-archivio-changed", fetchArch); };
  }, [user, can]);
  useEffect(() => {
    if (!user || user.role === "cliente") return;
    const fetchUnread = () => api.get("/studio/mail/unread-counts").then(({ data }) => setMailUnread(data)).catch(() => {});
    const onVis = () => { if (document.visibilityState === "visible") fetchUnread(); };
    fetchUnread();
    const id = setInterval(fetchUnread, 60 * 1000);
    window.addEventListener("pgp-mail-unread-changed", fetchUnread);
    window.addEventListener("pgp-mail-link-changed", fetchUnread);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(id);
      window.removeEventListener("pgp-mail-unread-changed", fetchUnread);
      window.removeEventListener("pgp-mail-link-changed", fetchUnread);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [user]);
  const [msgUnread, setMsgUnread] = useState(0);
  useEffect(() => {
    const onUnread = (e) => setMsgUnread(e.detail || 0);
    window.addEventListener(MSG_UNREAD_EVENT, onUnread);
    return () => window.removeEventListener(MSG_UNREAD_EVENT, onUnread);
  }, []);
  const [attUnread, setAttUnread] = useState(0);
  const [segMsgUnread, setSegMsgUnread] = useState(0);
  useEffect(() => {
    if (!user || user.role === "cliente" || user.role === "superadmin" || !can("messaggi")) return;
    const f = () => api.get("/studio/seg-messaggi/unread-count").then(({ data }) => setSegMsgUnread(data.count || 0)).catch(() => {});
    f();
    const id = setInterval(f, 60 * 1000);
    window.addEventListener("pgp-seg-msg-changed", f);
    return () => { clearInterval(id); window.removeEventListener("pgp-seg-msg-changed", f); };
  }, [user, can]);
  const [rinnovoNuove, setRinnovoNuove] = useState(0);
  useEffect(() => {
    if (user?.role !== "superadmin") return;
    const fetchRin = () => api.get("/admin/rinnovo-richieste/count-nuove").then(({ data }) => setRinnovoNuove(data.count || 0)).catch(() => {});
    fetchRin();
    const id = setInterval(fetchRin, 2 * 60 * 1000);
    window.addEventListener("pgp:rinnovo-changed", fetchRin);
    return () => { clearInterval(id); window.removeEventListener("pgp:rinnovo-changed", fetchRin); };
  }, [user]);
  const [compReqNuove, setCompReqNuove] = useState(0);
  useEffect(() => {
    if (user?.role !== "superadmin") return;
    const fetchReq = () => api.get("/admin/compagnie-richieste/count-nuove").then(({ data }) => setCompReqNuove(data.count || 0)).catch(() => {});
    fetchReq();
    const id = setInterval(fetchReq, 2 * 60 * 1000);
    window.addEventListener("pgp:compagnie-richieste-changed", fetchReq);
    return () => { clearInterval(id); window.removeEventListener("pgp:compagnie-richieste-changed", fetchReq); };
  }, [user]);
  useEffect(() => {
    const onAtt = (e) => setAttUnread(e.detail || 0);
    window.addEventListener(ATT_UNREAD_EVENT, onAtt);
    return () => window.removeEventListener(ATT_UNREAD_EVENT, onAtt);
  }, []);
  const [openKey, setOpenKey] = useState(
    (location.pathname.startsWith("/app/impostazioni") || location.pathname === "/app/bookmarklet-aruba") ? "impostazioni" : null
  );

  const [ver, setVer] = useState(null);
  useEffect(() => {
    api.get("/app-version").then(({ data }) => setVer(data)).catch(() => {});
  }, []);
  const verDate = ver?.updated_at ? ver.updated_at.split("-").reverse().join("/") : "";
  const offMods = useDisabledModules(!!user && !!user.tenant_id && user.role !== "superadmin" && user.role !== "cliente");

  let items = [];
  if (user.role === "superadmin") items = ADMIN_NAV;
  else if (user.role === "cliente") items = CLIENTE_NAV;
  else {
    items = STUDIO_NAV.filter((it) => {
      if (offMods.has(it.key)) return false;
      if (it.ownerOnly && !["titolare", "amministratore"].includes(user.role)) return false;
      if (it.perm) return can(it.perm);
      return true;
    });
  }

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const displayName =
    [user.nome, user.cognome].filter(Boolean).join(" ").trim() || user.name || "Utente";
  const initials =
    displayName
      .split(/\s+/)
      .map((s) => s[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?";

  const countFor = (key) => {
    if (key === "pratiche") return praticheAperte;
    if (key === "email" || key === "pec") return mailUnread[key] || 0;
    if (key === "messaggi") return msgUnread;
    if (key === "messaggi-segnalatori") return segMsgUnread;
    if (key === "richieste-rinnovo") return rinnovoNuove;
    if (key === "richieste-compagnie") return compReqNuove;
    return 0;
  };

  return (
    <aside
      className={`${collapsed ? "w-16" : ""} shrink-0 h-screen sticky top-0 flex flex-col border-r border-[#1e293b] transition-[width] duration-200`}
      style={{ background: "var(--pgp-sidebar)", ...(collapsed ? {} : { width: sideW }) }}
      data-testid="sidebar"
      data-collapsed={collapsed ? "1" : "0"}
    >
      <div className={`border-b border-[#1e293b] flex items-center ${collapsed ? "justify-center px-2 py-4" : "gap-3 px-5 py-5"}`}>
        <div className="h-9 w-9 rounded-lg bg-amber-500 flex items-center justify-center pgp-glow shrink-0">
          <Shield className="h-5 w-5 text-[#0a0d14]" strokeWidth={2.4} />
        </div>
        {!collapsed && (
          <div className="leading-tight">
            <div className="font-head font-extrabold text-[15px] tracking-tight text-slate-50">
              Patro<span className="text-amber-500">Gest</span>Pro
            </div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Infortunistica</div>
          </div>
        )}
        {!collapsed && (mailUnread.pending_suggestions || 0) > 0 && (
          <SuggestionsBell count={mailUnread.pending_suggestions} />
        )}
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {items.map((it) =>
          it.children ? (
            collapsed ? (
              <button
                key={it.key}
                type="button"
                data-testid={`nav-${it.key}`}
                title={it.label}
                onClick={() => navigate(it.path)}
                className={`w-full flex items-center justify-center rounded-lg px-0 py-2.5 transition-colors ${
                  location.pathname.startsWith(it.path)
                    ? "bg-cyan-500/15 text-cyan-400 border border-cyan-500/30"
                    : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/60 border border-transparent"
                }`}
              >
                <Icon name={it.icon} className="h-[18px] w-[18px]" />
              </button>
            ) : (
            <div key={it.key}>
              <button
                type="button"
                data-testid={`nav-${it.key}`}
                onClick={() => {
                  setOpenKey((k) => (k === it.key ? null : it.key));
                  navigate(it.path);
                }}
                className={`w-full group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${
                  location.pathname.startsWith(it.path)
                    ? "bg-cyan-500/15 text-cyan-400 border border-cyan-500/30 font-medium"
                    : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/60 border border-transparent"
                }`}
              >
                <Icon name={it.icon} className="h-[18px] w-[18px]" />
                <span className="flex-1 truncate text-left">{it.label}</span>
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${openKey === it.key ? "rotate-180" : ""}`}
                />
              </button>
              {openKey === it.key && (() => {
                const firstHash = it.children.find((c) => (c.to || "").includes("#"));
                const defaultHash = firstHash ? `#${firstHash.to.split("#")[1]}` : "";
                const childActive = (to) => {
                  const [p, h] = to.split("#");
                  if (location.pathname !== p) return false;
                  if (!h) return true;
                  return (location.hash || defaultHash) === `#${h}`;
                };
                return (
                  <div
                    className="mt-1 ml-4 pl-3 border-l border-slate-800 space-y-0.5"
                    data-testid={`submenu-${it.key}`}
                  >
                    {it.children.map((c) => {
                      if (c.action === "remote-support") return <RemoteSupportMenuItem key={c.key} label={c.label} testId={`subnav-${c.key}`} />;
                      const active = childActive(c.to);
                      return (
                        <NavLink
                          key={c.key}
                          to={c.to}
                          data-testid={`subnav-${c.key}`}
                          className={`block rounded-md px-3 py-2 text-[13px] transition-colors truncate ${
                            active
                              ? "bg-cyan-500/10 text-cyan-400 font-medium"
                              : "text-slate-400 hover:text-cyan-400 hover:bg-slate-800/50"
                          }`}
                        >
                          {c.label}
                        </NavLink>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
            )
          ) : (
            <NavLink
              key={it.key}
              to={it.path}
              data-testid={`nav-${it.key}`}
              title={collapsed ? it.label : undefined}
              className={({ isActive }) =>
                `group flex items-center rounded-lg text-sm transition-colors ${collapsed ? "justify-center px-0 py-2.5" : "gap-3 px-3 py-2.5"} ${
                  isActive
                    ? "bg-cyan-500/15 text-cyan-400 border border-cyan-500/30 font-medium"
                    : it.key === "pratiche"
                    ? "bg-yellow-200/90 text-slate-900 hover:bg-yellow-200 border border-yellow-300/60 font-medium"
                    : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/60 border border-transparent"
                }`
              }
            >
              <span className="relative flex items-center">
                <Icon name={it.icon} className="h-[18px] w-[18px]" />
                {collapsed && countFor(it.key) > 0 && (
                  <span data-testid={`nav-dot-${it.key}`} className="absolute -top-1.5 -right-2 min-w-[15px] h-[15px] px-1 flex items-center justify-center text-[9px] font-bold font-mono bg-amber-500 text-[#0a0d14] rounded-full">{countFor(it.key)}</span>
                )}
              </span>
              {!collapsed && <span className="flex-1 truncate">{it.label}</span>}
              {!collapsed && it.key === "archivio" && praticheArchiviate > 0 && (
                <span data-testid="nav-count-archivio" title="Pratiche archiviate" className="text-[10px] font-mono font-normal bg-slate-700 text-slate-100 rounded-full px-1.5 py-0.5">{praticheArchiviate}</span>
              )}
              {!collapsed && it.key === "pratiche" && praticheAperte > 0 && (
                <span data-testid="nav-count-pratiche" title="Pratiche aperte in gestione" className="text-[10px] font-mono bg-amber-500 text-[#0a0d14] rounded-full px-1.5 py-0.5 font-semibold">{praticheAperte}</span>
              )}
              {!collapsed && it.key === "pratiche" && attUnread > 0 && (
                <span data-testid="nav-unread-allegati" title="Nuovi allegati dai clienti" className="inline-flex items-center gap-0.5 text-[10px] font-mono bg-amber-500 text-[#0a0d14] rounded-full px-1.5 py-0.5 font-semibold"><Paperclip className="h-2.5 w-2.5" />{attUnread}</span>
              )}
              {!collapsed && (it.key === "email" || it.key === "pec") && mailUnread[it.key] > 0 && (
                <span data-testid={`nav-unread-${it.key}`} className="text-[10px] font-mono bg-amber-500 text-[#0a0d14] rounded-full px-1.5 py-0.5">{mailUnread[it.key]}</span>
              )}
              {!collapsed && it.key === "messaggi" && msgUnread > 0 && (
                <span data-testid="nav-unread-messaggi" title="Messaggi non letti" className="text-[10px] font-mono bg-amber-500 text-[#0a0d14] rounded-full px-1.5 py-0.5 font-semibold">{msgUnread}</span>
              )}
              {!collapsed && it.key === "messaggi-segnalatori" && segMsgUnread > 0 && (
                <span data-testid="nav-unread-messaggi-segnalatori" title="Messaggi segnalatori non letti" className="text-[10px] font-mono bg-amber-500 text-[#0a0d14] rounded-full px-1.5 py-0.5 font-semibold">{segMsgUnread}</span>
              )}
              {!collapsed && it.key === "richieste-rinnovo" && rinnovoNuove > 0 && (
                <span data-testid="nav-count-richieste-rinnovo" title="Richieste di rinnovo nuove" className="text-[10px] font-mono bg-amber-500 text-[#0a0d14] rounded-full px-1.5 py-0.5 font-semibold">{rinnovoNuove}</span>
              )}
              {!collapsed && it.key === "richieste-compagnie" && compReqNuove > 0 && (
                <span data-testid="nav-count-richieste-compagnie" title="Richieste di nuove compagnie" className="text-[10px] font-mono bg-amber-500 text-[#0a0d14] rounded-full px-1.5 py-0.5 font-semibold">{compReqNuove}</span>
              )}
              {!collapsed && it.ready === false && (
                <span className="text-[9px] uppercase tracking-wide text-slate-500 border border-slate-700 rounded px-1 py-0.5 font-mono">
                  soon
                </span>
              )}
            </NavLink>
          )
        )}
      </nav>

      <div className={`border-t border-[#1e293b] p-3 ${collapsed ? "flex flex-col items-center gap-2" : ""}`}>
        <div className={`flex items-center rounded-lg ${collapsed ? "justify-center" : "gap-3 px-2 py-2"}`}>
          <div className="h-9 w-9 rounded-full bg-slate-700 flex items-center justify-center text-xs font-semibold text-slate-200" title={collapsed ? `${displayName} · ${ROLE_LABELS[user.role]}` : undefined}>
            {initials}
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0 leading-tight">
              <div className="text-sm font-medium text-slate-100 truncate" data-testid="sidebar-user-name">
                {displayName}
              </div>
              <div className="text-[11px] text-amber-500/80 font-mono">{ROLE_LABELS[user.role]}</div>
            </div>
          )}
        </div>
        <button
          onClick={() => setProfiloOpen(true)}
          data-testid="profilo-button"
          title="Il mio profilo"
          className={`flex items-center rounded-lg text-sm text-slate-400 hover:text-amber-400 hover:bg-amber-500/10 transition-colors ${collapsed ? "justify-center h-9 w-9" : "mt-1 w-full gap-2 px-3 py-2"}`}
        >
          <UserCog className="h-4 w-4" />
          {!collapsed && "Il mio profilo"}
        </button>
        <FirmaGraficaDialog open={profiloOpen} onOpenChange={setProfiloOpen} />
        <button
          onClick={handleLogout}
          data-testid="logout-button"
          title="Esci"
          className={`flex items-center rounded-lg text-sm text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors ${collapsed ? "justify-center h-9 w-9" : "mt-1 w-full gap-2 px-3 py-2"}`}
        >
          <LogOut className="h-4 w-4" />
          {!collapsed && "Esci"}
        </button>
        {ver && (
          <div className={`mt-2 text-[10px] text-slate-400 select-none pointer-events-none ${collapsed ? "text-center" : "px-3"}`} data-testid="sidebar-app-version">
            {collapsed ? ver.version : `${ver.version} — Aggiornato il ${verDate}`}
          </div>
        )}
      </div>
      {!collapsed && <ResizeHandle width={sideW} onChange={setSideW} testId="sidebar-resize-handle" />}
    </aside>
  );
}
